import { useRef, type ReactNode } from "react";
import { Button } from "../shadcn/Button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../shadcn/Dialog";

export interface ConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  dismissLabel?: string;
  onConfirm: () => void;
  destructive?: boolean;
  pending?: boolean;
  confirmDisabled?: boolean;
  error?: string;
}

export function ConfirmationDialog({
  open, onOpenChange, title, description, confirmLabel, dismissLabel = "Keep",
  onConfirm, destructive = false, pending = false, confirmDisabled = false, error,
}: ConfirmationDialogProps) {
  const dismissRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!pending) onOpenChange(nextOpen); }}>
      <DialogContent
        role="alertdialog"
        showCloseButton={false}
        aria-busy={pending}
        onOpenAutoFocus={(event) => {
          previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
          event.preventDefault();
          dismissRef.current?.focus();
        }}
        onCloseAutoFocus={(event) => {
          if (previousFocus.current?.isConnected) {
            event.preventDefault();
            previousFocus.current.focus();
          }
        }}
        onPointerDownOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => { if (pending) event.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="pr-0">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button ref={dismissRef} variant="outline" disabled={pending} onClick={() => onOpenChange(false)}>{dismissLabel}</Button>
          <Button variant={destructive ? "destructive" : "default"} disabled={pending || confirmDisabled} onClick={onConfirm}>
            {pending ? "Please wait…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
